import client from './client.js'

/**
 * Run the full 3-step direct-to-R2 KYC document upload flow.
 * Returns KycDocumentResponse on success.
 *
 * @param {string}  groupId
 * @param {string}  documentType   - enum value, e.g. "ID_CARD"
 * @param {string}  issuingCountry - ISO 3166-1 alpha-2, optional
 * @param {File}    file
 * @param {boolean} frontSide
 * @returns {Promise<Object>}
 */
export async function uploadFile(groupId, documentType, issuingCountry, file, frontSide = false) {
  // Step 1: initialize (presigned PUT URL)
  const initResp = await initializeKycDocumentUpload(groupId, {
    documentType,
    issuingCountry,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    frontSide,
  })

  // Step 2: PUT file bytes directly to R2
  const putResp = await uploadDirectToR2(
    initResp.uploadUrl, file, initResp.allowedContentTypes || file.type
  )
  if (!putResp.ok) {
    const errText = await putResp.text()
    throw new Error(`R2 upload failed (${putResp.status}): ${errText}`)
  }

  // Step 3: confirm with backend
  const docResp = await confirmKycDocumentUpload(groupId, initResp.uploadSessionId, {
    sha256Hash: '',
    uploadedSizeBytes: file.size,
  })

  return docResp
}

/**
 * returns { uploadSessionId, documentId, objectKey, uploadUrl, expiresAt, maxSizeBytes, allowedContentTypes }
 */
export function initializeKycDocumentUpload(groupId, payload) {
  return client.post('kyc/documents/initialize?groupId=' + groupId, payload)
    .then(res => res.data)
}

/**
 * PUT file bytes directly to R2 using the presigned URL from initialize.
 * Returns the Response object (call response.ok to check success).
 */
export async function uploadDirectToR2(uploadUrl, file, contentType) {
  return fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  })
}

/**
 * Notify the backend that the R2 upload is complete.
 * payload: { sha256Hash: string, uploadedSizeBytes: number }
 */
export function confirmKycDocumentUpload(groupId, sessionId, payload) {
  return client.post(
    'kyc/documents/' + sessionId + '/confirm?groupId=' + groupId,
    payload
  ).then(res => res.data)
}

/**
 * Fetch all documents for a KYC group.
 * Returns: Array<KycDocumentResponse>
 */
export function fetchKycDocuments(groupId) {
  return client.get('kyc/documents', { params: { groupId } })
    .then(res => res.data)
}

/**
 * Get a temporary view URL for a reviewer/admin to inspect a document.
 */
export function getDocumentViewUrl(groupId, documentId) {
  return client.get('kyc/documents/' + documentId + '/view-url', { params: { groupId } })
    .then(res => res.data)
}